import {
  BaseWebComponentController,
  IListenerConfig,
  WebComponentEvent,
} from '../../../framework/BaseWebComponentController';
import { LicenseBasicMonthlySubscriptionSubFormWebComponent } from '../component/LicenseBasicMonthlySubscriptionSubFormWebComponent';
import { LicenseBasicMonthlySubscriptionSubFormView } from '../view/LicenseBasicMonthlySubscriptionSubFormView';
import { templateLicenseBasicMonthlySubscriptionSubForm } from '../view/template';
import { InputEvents } from '../../../ui/gzInput/types';
import { CompanyData } from '../../../company/types/company-data';
import { CompanyActionContext } from '../../../company/common/types/CompanyActionContext';
import { DisableReservedSeatsOptionConfirmationWindow } from '../../../company/common/disableReservedSeatsOptionConfirmationWindow/DisableReservedSeatsOptionConfirmationWindow';
import { FormEvent } from '../../../ui/gzForm/formField/types/form-event.enum';

export class LicenseBasicMonthlySubscriptionSubFormController extends BaseWebComponentController {
  public component: LicenseBasicMonthlySubscriptionSubFormWebComponent;
  public view: LicenseBasicMonthlySubscriptionSubFormView;
  public viewConstructor: typeof LicenseBasicMonthlySubscriptionSubFormView;

  private isDisableReservedSeatsOptionConfirmationWindowAllowed = false;

  constructor(component: LicenseBasicMonthlySubscriptionSubFormWebComponent) {
    super(component);

    this.initialSetup({
      viewConstructor: LicenseBasicMonthlySubscriptionSubFormView,
      viewTemplate: templateLicenseBasicMonthlySubscriptionSubForm,
    });
  }

  computeEventListeners(): IListenerConfig[] {
    return [
      {
        element: this.view.dom.$reserveSeatsFormField,
        event: InputEvents.CHANGE,
        callback: this.onReserveSeatsValueChange,
      },

      {
        element: this.view.dom.$reserveSeatsFormField,
        event: FormEvent.ToggleableChange,
        callback: this.onToggleReserveSeats,
      },

      {
        element: this.view.dom.$subscriptionEndDateFormField,
        event: FormEvent.ToggleableChange,
        callback: this.updateAutoRenewalFormFieldAvailability,
      },
    ];
  }

  public setCompanyData(company: CompanyData) {
    this.view.setCompanyData(company);

    // Setting initial triggers for dialog from company data
    this.setUpDisableReservedSeatsOptionConfirmationWindow(company);
  }

  private onReserveSeatsValueChange(): void {
    this.view.updateMinCommitmentValidators();
    this.view.updateAvailableSeatsText();
  }

  private onToggleReserveSeats(event: WebComponentEvent<{ isOn: boolean; isUserAction: boolean }>): void {
    const isOn = event.detail.isOn;
    const reserveSeatsFormGroup = this.view.formGroup?.getFormChild(['specifics', 'hasReservedSlots']);

    this.view.updateMinCommitmentValidators();
    this.view.updateAvailableSeatsText();

    if (!reserveSeatsFormGroup) {
      return;
    }

    const reserveSeatsValueBeforeChange = reserveSeatsFormGroup.value;
    this.view.updateHasReservedSlotsValue();

    const toggleChanged = reserveSeatsFormGroup.value !== reserveSeatsValueBeforeChange;
    if (toggleChanged && event.detail.isUserAction) {
      this.createAndShowDisableReservedSeatsOptionConfirmationWindowIfAllowed(isOn);
    }
  }

  private updateAutoRenewalFormFieldAvailability(event: CustomEvent<{ isUserAction: boolean }>): void {
    if (!event.detail.isUserAction) {
      return;
    }

    this.view.updateAutoRenewalFormFieldAvailability();
  }

  private setUpDisableReservedSeatsOptionConfirmationWindow(company: CompanyData) {
    if (
      this.view.viewData?.context !== CompanyActionContext.Edit ||
      !CompanyData.hasReservedSeatsForUnderneathCompanies(company)
    ) {
      return;
    }
    this.isDisableReservedSeatsOptionConfirmationWindowAllowed =
      CompanyData.hasReservedSeatsForUnderneathCompanies(company);
  }

  private createAndShowDisableReservedSeatsOptionConfirmationWindowIfAllowed(reservedSeatsToggleIsOn: boolean) {
    if (reservedSeatsToggleIsOn || !this.isDisableReservedSeatsOptionConfirmationWindowAllowed) {
      // Set the field agreeRemoveReservedSlotsForChildren to default value false if
      // is not the case to remove reserved seats either because the toggle is switched on or confirmation is not allowed
      this.view.setAgreeRemoveReservedSlotsForChildren(false);
      return;
    }

    const cancelCallback = () => {
      this.view.setAgreeRemoveReservedSlotsForChildren(false);
      this.view.updateReservedSeatsFormFieldAvailability(!reservedSeatsToggleIsOn);
    };

    const confirmCallback = () => {
      this.view.setAgreeRemoveReservedSlotsForChildren(true);
    };

    DisableReservedSeatsOptionConfirmationWindow.showDisableReservedSeatsOptionConfirmationWindow(
      this.dependencies.translateFn,
      cancelCallback,
      confirmCallback
    );
  }
}
